/**
 * Optimistic Update Hook with Rollback
 * 
 * Provides a generic system for optimistic updates that immediately update the UI
 * while performing the server update in the background. If the server update fails,
 * the UI automatically rolls back to the previous state and shows an error.
 * 
 * Key Features:
 * - Immediate UI feedback (optimistic)
 * - Automatic rollback on failure
 * - Loading states during server sync
 * - Error handling with user feedback
 * - TypeScript support for type safety
 * - React Query cache integration
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { notifications } from '../../config/toast-notifications.js';

export interface OptimisticUpdateOptions<TData, TVariables> {
  /** 
   * The mutation function that performs the server update 
   * Should return the updated data from server
   */
  mutationFn: (variables: TVariables) => Promise<TData>;
  
  /** 
   * Function to apply optimistic update to local data
   * Receives current data and variables, returns optimistically updated data
   */
  optimisticUpdate: (currentData: TData, variables: TVariables) => TData;
  
  /** 
   * Query keys to invalidate after successful update
   * Can be a single key or array of keys
   */
  invalidateKeys?: string[][] | string[];
  
  /** 
   * Optional success message to show user
   * Can be a string or function that receives the updated data
   */
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  
  /** 
   * Optional error message to show user
   * Can be a string or function that receives the error
   */
  errorMessage?: string | ((error: Error, variables: TVariables) => string);
  
  /** 
   * Whether to show toast notifications
   * @default true
   */
  showToasts?: boolean;
  
  /** 
   * Callback fired on successful update (after server confirms)
   */
  onSuccess?: (data: TData, variables: TVariables) => void;
  
  /** 
   * Callback fired on update failure (after rollback)
   */
  onError?: (error: Error, variables: TVariables) => void;
  
  /** 
   * Custom rollback function - if not provided, will use original data
   */
  customRollback?: (originalData: TData, variables: TVariables) => TData;
}

export interface OptimisticUpdateResult<TData, TVariables> {
  /** Execute the optimistic update */
  execute: (currentData: TData, variables: TVariables) => Promise<TData>;
  
  /** Whether the server update is in progress */
  isPending: boolean;
  
  /** Error from the last failed update */
  error: Error | null;
  
  /** Whether currently in rollback state */
  isRollingBack: boolean;
  
  /** Reset error state */
  resetError: () => void;
}

/**
 * Hook for performing optimistic updates with automatic rollback
 * 
 * @example
 * ```typescript
 * const optimisticUpdate = useOptimisticUpdate({
 *   mutationFn: ({ lotId, statusId }) => lotsExtensions.update(lotId, { status_id: statusId }),
 *   optimisticUpdate: (currentLot, { statusId }) => ({ ...currentLot, status_id: statusId }),
 *   invalidateKeys: [['lots'], ['lot-status-management']],
 *   successMessage: (lot, { statusId }) => `Lot status updated successfully`,
 *   errorMessage: 'Failed to update lot status'
 * });
 * 
 * // In component:
 * const handleStatusChange = async (newStatus) => {
 *   try {
 *     await optimisticUpdate.execute(currentLot, { lotId, statusId: newStatus.id });
 *   } catch (error) {
 *     // Error handled automatically with rollback and toast
 *   }
 * };
 * ```
 */
export function useOptimisticUpdate<TData, TVariables>({
  mutationFn,
  optimisticUpdate,
  invalidateKeys = [],
  successMessage,
  errorMessage = 'Update failed. Changes have been reverted.',
  showToasts = true,
  onSuccess,
  onError,
  customRollback
}: OptimisticUpdateOptions<TData, TVariables>): OptimisticUpdateResult<TData, TVariables> {
  
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  
  // Store original data for rollback
  const originalDataRef = useRef<TData | null>(null);
  const rollbackCallbackRef = useRef<((data: TData) => void) | null>(null);
  
  const execute = useCallback(async (
    currentData: TData, 
    variables: TVariables,
    rollbackCallback?: (data: TData) => void
  ): Promise<TData> => {
    // Store original data and rollback callback
    originalDataRef.current = currentData;
    rollbackCallbackRef.current = rollbackCallback || null;
    
    // Clear previous error
    setError(null);
    setIsPending(true);
    
    // Apply optimistic update immediately
    const optimisticData = optimisticUpdate(currentData, variables);
    
    // If we have a rollback callback (for local state), call it with optimistic data
    rollbackCallback?.(optimisticData);
    
    try {
      // Perform server update
      const serverData = await mutationFn(variables);
      
      // Server update succeeded
      setIsPending(false);
      
      // Invalidate cache keys
      const keysToInvalidate = Array.isArray(invalidateKeys[0]) ? invalidateKeys as string[][] : [invalidateKeys as string[]];
      keysToInvalidate.forEach(key => {
        if (key.length > 0) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      });
      
      // Show success toast ONLY after server confirms (not during optimistic update)
      if (showToasts && successMessage) {
        const message = typeof successMessage === 'function'
          ? successMessage(serverData, variables)
          : successMessage;

        notifications.success("Success", message);
      }
      
      // Call success callback
      onSuccess?.(serverData, variables);
      
      return serverData;
      
    } catch (serverError) {
      // Server update failed - perform rollback
      setIsRollingBack(true);
      setIsPending(false);
      
      const rollbackData = customRollback 
        ? customRollback(originalDataRef.current!, variables)
        : originalDataRef.current!;
      
      // Rollback local state
      rollbackCallbackRef.current?.(rollbackData);
      
      // Set error
      const error = serverError instanceof Error ? serverError : new Error('Update failed');
      setError(error);
      
      // Show error toast
      if (showToasts) {
        const message = typeof errorMessage === 'function'
          ? errorMessage(error, variables)
          : errorMessage;

        notifications.error("Update Failed", message);
      }
      
      // Call error callback
      onError?.(error, variables);
      
      // Reset rolling back state after a short delay for visual feedback
      setTimeout(() => setIsRollingBack(false), 300);
      
      // Re-throw for caller to handle if needed
      throw error;
    }
  }, [mutationFn, optimisticUpdate, invalidateKeys, successMessage, errorMessage, showToasts, onSuccess, onError, customRollback, queryClient]);
  
  const resetError = useCallback(() => {
    setError(null);
    setIsRollingBack(false);
  }, []);
  
  return {
    execute,
    isPending,
    error,
    isRollingBack,
    resetError
  };
}

/**
 * Specialized version for status updates with common patterns
 */
export interface StatusUpdateOptions<TEntity> {
  /** Entity type for error messages */
  entityType: string;
  /** Function to update entity on server - now receives current entity for required field population */
  updateFn: (entityId: string, statusId: string, statusName?: string, statusColor?: string | null, currentEntity?: TEntity) => Promise<TEntity>;
  /** Function to get current status from entity */
  getCurrentStatus: (entity: TEntity) => { id: string; name: string };
  /** Function to create updated entity with new status */
  createUpdatedEntity: (entity: TEntity, statusId: string, statusName: string, statusColor?: string | null) => TEntity;
  /** Cache keys to invalidate */
  invalidateKeys: string[][];
}

export function useOptimisticStatusUpdate<TEntity>({
  entityType,
  updateFn,
  getCurrentStatus,
  createUpdatedEntity,
  invalidateKeys
}: StatusUpdateOptions<TEntity>) {
  // Use a ref to store the current entity so it's available to mutationFn
  const currentEntityRef = useRef<TEntity | null>(null);

  return useOptimisticUpdate<TEntity, { entityId: string; statusId: string; statusName: string; statusColor?: string | null; currentEntity?: TEntity }>({
    mutationFn: async ({ entityId, statusId, statusName, statusColor, currentEntity }) =>
      updateFn(entityId, statusId, statusName, statusColor, currentEntity),
    optimisticUpdate: (currentEntity, { statusId, statusName, statusColor }) => {
      // Store current entity in ref for potential future use
      currentEntityRef.current = currentEntity;
      return createUpdatedEntity(currentEntity, statusId, statusName, statusColor);
    },
    invalidateKeys,
    successMessage: (entity, { statusName }) => `${entityType} status updated to "${statusName}"`,
    errorMessage: (error, variables) => `Failed to update ${entityType.toLowerCase()} status. Changes have been reverted.`
  });
}